import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { HeartIcon as Heart, VideoCameraIcon as VideoCamera, ArrowRightIcon as ArrowRight, PlusIcon as Plus, CalendarIcon as Calendar, PawPrintIcon as PawPrint } from "@phosphor-icons/react";
import { LuxuryCard } from "@/components/luxury";

/**
 * Breeder dashboard hero card — surfaces "Planned Breedings" + "Upcoming
 * Litters" + "Live Video Tours" front and center so working breeders
 * don't have to dig through nav to find their most important workflows.
 *
 * Why this exists: per the founder, breeders kept telling her "I can't
 * find where to publish a planned breeding" or "I forgot we even have
 * video tours". These features WERE shipped but were buried two clicks
 * deep. This single card puts them above the fold on the breeder
 * dashboard with live counts so the breeder sees momentum at a glance.
 *
 * Renders ONLY for non-buyer, non-transporter personas (so breeders
 * AND owner-breeders see it; pure buyers and transporters don't).
 *
 * Data sources:
 *   - GET /api/upcoming-breedings/me → planned/expecting list
 *   - GET /api/video-tours/mine     → live tour stats (best-effort)
 */
export default function BreederBreedingHubCard({ persona }) {
  const [breedings, setBreedings] = useState(null);
  const [toursCount, setToursCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/upcoming-breedings/me", { silent: true })
      .then((r) => { if (!cancelled) setBreedings(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setBreedings([]); });
    api.get("/video-tours/mine", { silent: true })
      .then((r) => {
        if (cancelled) return;
        // Endpoint returns { sent_requests, received_requests } or a list
        // — coerce to a single count so the card always has a number.
        const d = r.data || {};
        if (Array.isArray(d)) setToursCount(d.length);
        else if (typeof d.received_requests?.length === "number") setToursCount(d.received_requests.length);
        else if (typeof d.count === "number") setToursCount(d.count);
        else setToursCount(0);
      })
      .catch(() => { if (!cancelled) setToursCount(0); });
    return () => { cancelled = true; };
  }, []);

  // Buyers and transporters never plan breedings — skip the widget.
  if (persona === "buyer" || persona === "transporter") return null;

  const planned   = (breedings || []).filter((b) => b.stage === "planned").length;
  const expecting = (breedings || []).filter((b) => b.stage === "expecting").length;
  const whelped   = (breedings || []).filter((b) => b.stage === "whelped").length;
  const isEmpty   = breedings !== null && planned + expecting + whelped === 0;

  return (
    <LuxuryCard
      className="p-5 sm:p-7 mb-8 relative overflow-hidden"
      data-testid="breeder-breeding-hub"
    >
      {/* Gold corner accent + diagonal sheen for visual hierarchy */}
      <span aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gold-gradient opacity-[0.06] blur-2xl" />
      <span aria-hidden className="pointer-events-none absolute inset-0 luxury-grain mix-blend-overlay opacity-[0.04]" />

      <div className="flex flex-col gap-5 lg:flex-row lg:items-stretch lg:gap-8 relative">
        {/* Left: headline + counts */}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2 flex items-center gap-1.5">
            <Heart size={11} weight="fill" className="text-gold-dark" />
            Breeding hub
          </p>
          <h2 className="font-spectral font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.1] text-ink mb-2">
            Plan a breeding. Fill the waitlist. Whelp on time.
          </h2>
          <p className="text-[13.5px] text-[#4A4A4A] leading-relaxed max-w-xl mb-5">
            Publish a planned breeding the moment you make the match.
            Collect waitlist deposits straight to your Stripe payouts.
            When the pups arrive, the listing becomes a real litter
            with one row per puppy — ready to assign to buyers.
          </p>

          {/* Live counts */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5 max-w-md" data-testid="breeding-hub-counts">
            <CountTile
              icon={Calendar}
              value={breedings === null ? "—" : planned}
              label="Planned"
              testId="breeding-hub-count-planned"
            />
            <CountTile
              icon={Heart}
              value={breedings === null ? "—" : expecting}
              label="Expecting"
              testId="breeding-hub-count-expecting"
            />
            <CountTile
              icon={PawPrint}
              value={breedings === null ? "—" : whelped}
              label="Whelped"
              testId="breeding-hub-count-whelped"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/litters/upcoming/new"
              data-testid="breeding-hub-plan-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 h-11 transition-colors"
            >
              <Plus size={14} weight="bold" /> Plan a breeding
            </Link>
            <Link
              to="/litters"
              data-testid="breeding-hub-list-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] hover:border-ink bg-white text-ink text-[13px] font-semibold px-4 h-11 transition-colors"
            >
              My litters <ArrowRight size={12} weight="bold" />
            </Link>
            <Link
              to="/marketplace/upcoming"
              data-testid="breeding-hub-browse-btn"
              className="text-[12px] text-[#6B6B6B] hover:text-ink underline underline-offset-2"
            >
              Browse public upcoming litters
            </Link>
          </div>
          {isEmpty && (
            <p className="mt-4 text-[12px] text-[#8A6A00] bg-[#FFF8E5] border border-[#E8D9A6] rounded-lg px-3 py-2 inline-block">
              <strong>Tip:</strong> Publishing a planned breeding builds your waitlist before the puppies even arrive.
            </p>
          )}
        </div>

        {/* Right: Video tour CTA — a separate, high-contrast panel so the
            "where do I start a video?" question (the founder explicitly
            flagged this) is answered above the fold without scrolling. */}
        <Link
          to="/tours"
          data-testid="breeding-hub-video-tours"
          className="group relative w-full lg:w-[280px] lg:flex-shrink-0 rounded-2xl border-2 border-[#C9A227]/40 bg-gradient-to-br from-[#FBF7EC] to-[#F6EAC4] p-5 hover:border-[#C9A227] transition-colors block"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-ink text-gold">
              <VideoCamera size={18} weight="duotone" />
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark">
              Live video tours
            </p>
          </div>
          <h3 className="font-spectral text-lg leading-tight text-ink mb-1.5">
            Show the kennel. Close the sale.
          </h3>
          <p className="text-[12.5px] text-[#4A4A4A] leading-relaxed">
            15-minute live video walk-throughs of your dogs and puppies —
            buyers pay $35 up front, you earn{" "}
            <strong className="text-ink">up to 80%</strong> per tour.
            Browser-based, no app installs, runs on Daily.co.
          </p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-ink/70">
              {toursCount === null ? "Loading…" : `${toursCount} tour${toursCount === 1 ? "" : "s"} this account`}
            </span>
            <span className="inline-flex items-center gap-1 text-[12px] font-bold text-ink group-hover:text-crimson transition-colors">
              Open tours <ArrowRight size={11} weight="bold" />
            </span>
          </div>
        </Link>
      </div>
    </LuxuryCard>
  );
}

function CountTile({ icon: IconCmp, value, label, testId }) {
  return (
    <div
      className="rounded-xl border border-[#E5E5E5] bg-white px-3 py-2.5 flex flex-col"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[#6B6B6B] mb-1">
        <IconCmp size={12} weight="duotone" />
        <span className="text-[9.5px] font-bold uppercase tracking-[0.16em]">{label}</span>
      </div>
      <span className="font-spectral text-2xl font-light text-ink leading-none font-tabular">
        {value}
      </span>
    </div>
  );
}
